import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Upload, Loader2, Wand2, FileText, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";

const POLL_INTERVAL = 3000; // 3 seconds

const DocumentImporter = ({ sessionType, onFieldsGenerated, patientId }) => {
  const { t } = useI18n();
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const ALLOWED_EXTENSIONS = ["txt", "pdf"];

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const ext = selected.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(t("docImport.invalidFormat"));
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      toast.error(t("docImport.fileTooLarge"));
      return;
    }
    setFile(selected);
  };

  const resetFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const pollForResult = async (generationId) => {
    const maxAttempts = 120; // 6 minutes max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      setProgress(Math.min(((i + 1) / 100) * 100, 95));

      try {
        const res = await axios.get(`${API}/session-reports/generation/${generationId}`, {
          withCredentials: true,
        });

        if (res.data.status === "completed") {
          onFieldsGenerated(res.data.fields, res.data.suggested_exercises || [], res.data.transcription || "");
          toast.success(`${t("docImport.generated")}${res.data.route?.label ? ` · ${res.data.route.label}` : ""}`);
          resetFile();
          return;
        } else if (res.data.status === "error") {
          throw new Error(res.data.error || "Erreur de génération");
        }
        // else status === "processing", continue polling
      } catch (err) {
        if (err.response?.status === 404) continue; // Not ready yet
        throw err;
      }
    }
    throw new Error("Délai d'attente dépassé");
  };

  const generateReport = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);

    try {
      const formData = new FormData();
      formData.append("document", file, file.name);
      formData.append("session_type", sessionType);
      if (patientId) formData.append("patient_id", patientId);

      // Step 1: Start background generation
      const res = await axios.post(`${API}/session-reports/generate-from-document`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { generation_id } = res.data;
      if (!generation_id) throw new Error("Pas de generation_id reçu");

      // Step 2: Poll for result
      setProgress(5);
      await pollForResult(generation_id);

    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Erreur réseau";
      toast.error(`${t("docImport.error")}: ${detail}`);
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  if (processing) {
    return (
      <Card className="border-dashed border-2 border-primary/20" data-testid="document-importer">
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground font-medium">{t("docImport.generating")}</p>
            <p className="text-xs text-muted-foreground">{t("docImport.patience")}</p>
            <div className="w-full max-w-xs">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(progress, 95)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-1">
                {Math.round(Math.min(progress, 95))}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed border-2 border-primary/20" data-testid="document-importer">
      <CardContent className="pt-5 pb-4">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Wand2 className="w-4 h-4" />
            <span>{t("docImport.title")}</span>
          </div>

          {!file ? (
            <>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-document-btn">
                <Upload className="w-4 h-4 mr-2" />
                {t("docImport.upload")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.pdf"
                className="hidden"
                onChange={handleFileSelect}
              />
              <p className="text-xs text-muted-foreground">{t("docImport.formats")}</p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm bg-muted/50 px-3 py-2 rounded-lg">
                <FileText className="w-4 h-4 text-primary" />
                <span className="font-medium">{file.name}</span>
                <button onClick={resetFile} className="ml-1 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Button onClick={generateReport} data-testid="generate-report-btn">
                <Wand2 className="w-4 h-4 mr-2" />
                {t("docImport.generate")}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DocumentImporter;
